import { OrderStatus } from '@src/graphql/Order/Types'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import {
  StyledIonCol,
  StyledIonRow,
  StyledIonText,
  StyledIonTextError,
  StyledModal,
} from '@src/components/Mobile/Pages/Private/Orders/Detail/Detail.styles'
import { Form as StyledForm } from '@src/components/Elements/Wrapper/Form'
import { IonGrid, IonLabel } from '@ionic/react'
import { StyledInput } from '@src/components/Mobile/Containers/MapWrapper/DownloadDrawer/DownloadDrawer.styles'
import { Button } from 'antd'
import React, { Dispatch, SetStateAction, useMemo } from 'react'
import { useMilageValue } from '@src/components/Mobile/Providers/Milage'

export interface FormValues {
  milage: number | undefined
  deliveryNumber?: string
  weightFormNumber?: string
}

interface StatusChangeModalProps {
  nextStatus: OrderStatus | undefined
  isStatusChangeModalOpen: boolean
  setIsStatusChangeModalOpen: Dispatch<SetStateAction<boolean>>
  onSubmitStatusChangeModal: (
    milage: number,
    deliveryNumber: string | undefined,
    weightFormNumber: string | undefined,
  ) => void
  vehicleId?: string
  isNextStatusFinished?: boolean
}

export const StatusChangeModal = ({
  nextStatus,
  isStatusChangeModalOpen,
  setIsStatusChangeModalOpen,
  onSubmitStatusChangeModal,
  vehicleId,
  isNextStatusFinished = false,
}: StatusChangeModalProps) => {
  const { t } = useTranslation()
  const currentMilageValue = useMilageValue(vehicleId) ?? 0
  const initialValues: FormValues = useMemo(
    () => ({
      milage: undefined,
    }),
    [currentMilageValue],
  )

  const validationSchema = Yup.object().shape({
    milage: Yup.number()
      .required('mobile.pages.orders.detail.milage.errors.required')
      .min(currentMilageValue, 'mobile.pages.orders.detail.milage.errors.min'),
    deliveryNumber: isNextStatusFinished
      ? Yup.string().required('mobile.pages.orders.detail.deliveryNumber.errors.required')
      : Yup.string().notRequired(),
    weightFormNumber: Yup.string().notRequired(),
  })

  const {
    handleSubmit: handleFormSubmit,
    handleChange,
    handleBlur,
    values,
    isValid,
    errors,
    isSubmitting,
  } = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,

    onSubmit: async ({ milage, deliveryNumber, weightFormNumber }, { resetForm }) => {
      onSubmitStatusChangeModal(Number(milage), deliveryNumber, weightFormNumber)
      resetForm()
    },
  })

  return (
    <StyledModal
      title={t('mobile.pages.orders.detail.statusChangeButton.alert.header')}
      open={isStatusChangeModalOpen}
      onCancel={() => setIsStatusChangeModalOpen(false)}
      centered
      tabComponents={[
        {
          tabName: '',
          component: (
            <>
              <StyledForm onSubmit={handleFormSubmit}>
                <IonGrid>
                  <StyledIonRow>
                    <StyledIonCol>
                      <StyledIonText>
                        {t('mobile.pages.orders.detail.statusChangeButton.alert.subheader', {
                          newStatus: t(`general.status.${nextStatus}`),
                        })}
                      </StyledIonText>
                    </StyledIonCol>
                  </StyledIonRow>
                  <StyledIonRow>
                    <StyledIonCol>
                      <IonLabel>{t('mobile.pages.orders.detail.milage.label')}</IonLabel>
                      <div>
                        {t('mobile.pages.orders.detail.milage.current')}: {currentMilageValue}
                      </div>
                      <StyledInput
                        onChange={handleChange}
                        value={values.milage}
                        disabled={isSubmitting}
                        onBlur={handleBlur}
                        name='milage'
                      />
                      {errors.milage && <StyledIonTextError>{t(errors.milage.toString())}</StyledIonTextError>}
                    </StyledIonCol>
                  </StyledIonRow>
                  {isNextStatusFinished && (
                    <>
                      <StyledIonRow>
                        <StyledIonCol>
                          <IonLabel>{t('mobile.pages.orders.detail.deliveryNumber.label')}</IonLabel>
                          <StyledInput
                            onChange={handleChange}
                            value={values.deliveryNumber}
                            disabled={isSubmitting}
                            onBlur={handleBlur}
                            name='deliveryNumber'
                          />
                          {errors.deliveryNumber && (
                            <StyledIonTextError>{t(errors.deliveryNumber.toString())}</StyledIonTextError>
                          )}
                        </StyledIonCol>
                      </StyledIonRow>

                      <StyledIonRow>
                        <StyledIonCol>
                          <IonLabel>{t('mobile.pages.orders.detail.weightFormNumber.label')}</IonLabel>
                          <StyledInput
                            onChange={handleChange}
                            value={values.weightFormNumber}
                            disabled={isSubmitting}
                            onBlur={handleBlur}
                            name='weightFormNumber'
                          />
                        </StyledIonCol>
                      </StyledIonRow>
                    </>
                  )}
                </IonGrid>

                <StyledIonCol size='6'>
                  <Button onClick={() => setIsStatusChangeModalOpen(false)} disabled={isSubmitting}>
                    {t('general.cancel')}
                  </Button>
                </StyledIonCol>
                <StyledIonCol size='6'>
                  <Button type='primary' htmlType='submit' disabled={!isValid || isSubmitting}>
                    {t('general.confirm')}
                  </Button>
                </StyledIonCol>
              </StyledForm>
            </>
          ),
        },
      ]}
    />
  )
}
